/* ==========================================================================
   HUSSIENI HOME — REALITY WORKS
   Client-side paging, per-page media attachment, autoplay video, lightbox.

   Paging is client-side because Shopify Liquid cannot read query
   parameters — the `request` object has no url/query member, so a
   server-rendered ?page=N is impossible on a metaobject list. Liquid emits
   every tile as an empty shell; this file decides which page is visible and
   attaches real sources for that page only. Off-page media is never
   fetched, so this is also cheaper than the previous version.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init() {
    var root = document.querySelector('[data-hh-rw]');
    if (!root) return;

    var grid = root.querySelector('[data-hh-rw-grid]');
    if (!grid) return;

    var cells = Array.prototype.slice.call(grid.querySelectorAll('[data-hh-cell]'));
    var pages = parseInt(root.dataset.pages, 10) || 1;
    var current = 0;

    /* ---- media attachment -------------------------------------------- */
    function mount(cell) {
      if (cell.dataset.mounted) return;
      cell.dataset.mounted = '1';

      if (cell.dataset.type === 'image') {
        var img = new Image();
        img.className = 'hh-rw__img';
        img.decoding = 'async';
        img.alt = '';
        img.sizes = '(min-width: 1200px) 38vw, (min-width: 750px) 46vw, 92vw';
        img.srcset = cell.dataset.srcset || '';
        img.src = cell.dataset.src;
        img.addEventListener('load', function () { cell.classList.add('is-loaded'); });
        img.addEventListener('error', function () { cell.classList.add('is-failed'); });
        cell.appendChild(img);
        return;
      }

      if (cell.dataset.type === 'video' && cell.dataset.video) {
        var v = document.createElement('video');
        v.className = 'hh-rw__video';
        // Silent, inline, no chrome. Set as properties AND attributes:
        // iOS honours the attributes, everything else the properties.
        v.muted = true;
        v.volume = 0;
        v.loop = true;
        v.playsInline = true;
        v.setAttribute('muted', '');
        v.setAttribute('playsinline', '');
        v.setAttribute('loop', '');
        v.setAttribute('disablepictureinpicture', '');
        v.setAttribute('aria-hidden', 'true');
        v.tabIndex = -1;
        v.preload = 'metadata';
        if (cell.dataset.poster) v.poster = cell.dataset.poster;
        v.src = cell.dataset.video;
        v.addEventListener('loadeddata', function () { cell.classList.add('is-loaded'); });
        v.addEventListener('webkitbeginfullscreen', function (e) {
          e.preventDefault();
          if (v.webkitExitFullscreen) v.webkitExitFullscreen();
        });
        cell.appendChild(v);
      }
    }

    /* ---- reveal + playback ------------------------------------------- */
    var reveal = null;
    var playback = null;
    /* Clips are mounted only as they come within ~600px of the screen, so
       opening the page no longer downloads every video (it used to fetch
       about 2.6 MB of clips before the first photograph had loaded). */
    var near = null;
    if ('IntersectionObserver' in window) {
      near = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.dataset.wasNear = '1';
          mount(e.target);
          near.unobserve(e.target);
        });
      }, { rootMargin: '800px 0px 800px 0px' });
    }
    var eager = 0;
    function mountLater(cell) {
      if (!near) { mount(cell); return; }
      /* The feed sits below the opening photograph and the project index,
         so nothing in it is fetched until it is about 800px away: the
         opening photograph never has to share the connection. */
      near.observe(cell);
    }

    if ('IntersectionObserver' in window) {
      reveal = new IntersectionObserver(function (entries) {
        entries.forEach(function (e, n) {
          if (!e.isIntersecting) return;
          var d = reduced ? 0 : Math.min(n, 4) * 70;
          window.setTimeout(function () { e.target.classList.add('is-in'); }, d);
          reveal.unobserve(e.target);
        });
      }, { rootMargin: '0px 0px -6% 0px', threshold: 0.02 });

      playback = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          var v = e.target.querySelector('video');
          if (!v) return;
          if (e.isIntersecting) {
            v.muted = true;
            var p = v.play();
            if (p && p.catch) p.catch(function () {});
          } else if (!v.paused) {
            v.pause();
          }
        });
      }, { rootMargin: '15% 0px 15% 0px', threshold: 0.25 });
    }

    /* ---- page switching ----------------------------------------------- */
    function pageOf(cell) { return parseInt(cell.dataset.page, 10) || 1; }

    function showPage(n, opts) {
      opts = opts || {};
      if (n < 1) n = 1;
      if (n > pages) n = pages;
      if (n === current) return;
      current = n;

      root.classList.add('is-switching');

      window.setTimeout(function () {
        cells.forEach(function (cell) {
          var on = pageOf(cell) === n;
          cell.hidden = !on;
          if (!on) {
            cell.classList.remove('is-in');
            var v = cell.querySelector('video');
            if (v && !v.paused) v.pause();
            return;
          }
          // Mount only what this page actually needs.
          mountLater(cell);
          if (reveal) reveal.observe(cell); else cell.classList.add('is-in');
          if (playback && cell.dataset.type === 'video') playback.observe(cell);
        });

        root.classList.remove('is-switching');

        root.querySelectorAll('[data-hh-rw-page]').forEach(function (b) {
          var on = parseInt(b.dataset.hhRwPage, 10) === n;
          b.classList.toggle('is-current', on);
          if (on) b.setAttribute('aria-current', 'true');
          else b.removeAttribute('aria-current');
        });
        var bar = root.querySelector('[data-hh-rw-progress]');
        if (bar) bar.style.transform = 'scaleX(' + (n / pages) + ')';

        if (opts.scroll) {
          var top = root.getBoundingClientRect().top + window.scrollY - 72;
          window.scrollTo({ top: top, behavior: reduced ? 'auto' : 'smooth' });
        }
        if (opts.push) {
          var url = new URL(window.location.href);
          if (n === 1) url.searchParams.delete('page');
          else url.searchParams.set('page', n);
          window.history.pushState({ rw: n }, '', url.toString());
        }
      }, reduced ? 0 : 180);
    }

    root.querySelectorAll('[data-hh-rw-page]').forEach(function (b) {
      b.addEventListener('click', function () {
        showPage(parseInt(b.dataset.hhRwPage, 10), { scroll: true, push: true });
      });
    });
    var nextBtn = root.querySelector('[data-hh-rw-next]');
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        showPage(current >= pages ? 1 : current + 1, { scroll: true, push: true });
      });
    }

    window.addEventListener('popstate', function () {
      showPage(readPage(), { scroll: false, push: false });
    });

    function readPage() {
      var p = parseInt(new URLSearchParams(window.location.search).get('page'), 10);
      return (p && p >= 1 && p <= pages) ? p : 1;
    }

    // Direct link and refresh both land on the right page.
    showPage(readPage(), { scroll: false, push: false });

    initLightbox(root, cells);
  }

  /* ---- lightbox: images only ----------------------------------------- */
  function initLightbox(root, cells) {
    var lb = document.querySelector('[data-hh-lightbox]');
    if (!lb) return;
    var img = lb.querySelector('.hh-lb__img');
    var count = lb.querySelector('[data-hh-lb-count]');
    var imgs = cells.filter(function (c) { return c.dataset.type === 'image'; });
    var at = -1;
    var last = null;

    function show(i) {
      if (!imgs.length) return;
      if (i < 0) i = imgs.length - 1;
      if (i >= imgs.length) i = 0;
      at = i;
      img.src = imgs[i].dataset.full;
      if (count) count.textContent = (i + 1) + ' / ' + imgs.length;
    }
    function open(i) {
      last = document.activeElement;
      lb.hidden = false;
      lb.setAttribute('aria-hidden', 'false');
      show(i);
      requestAnimationFrame(function () {
        lb.classList.add('is-open');
        document.body.classList.add('hh-lb-open');
        lb.querySelector('[data-hh-lb-close]').focus();
      });
    }
    function close() {
      lb.classList.remove('is-open');
      document.body.classList.remove('hh-lb-open');
      window.setTimeout(function () {
        lb.hidden = true;
        lb.setAttribute('aria-hidden', 'true');
        img.removeAttribute('src');
      }, reduced ? 0 : 320);
      if (last) last.focus();
    }

    imgs.forEach(function (c, i) { c.addEventListener('click', function () { open(i); }); });
    lb.querySelector('[data-hh-lb-close]').addEventListener('click', close);
    lb.querySelector('[data-hh-lb-prev]').addEventListener('click', function () { show(at - 1); });
    lb.querySelector('[data-hh-lb-next]').addEventListener('click', function () { show(at + 1); });

    document.addEventListener('keydown', function (e) {
      if (lb.hidden) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') show(at - 1);
      else if (e.key === 'ArrowRight') show(at + 1);
      else if (e.key === 'Tab') { e.preventDefault(); lb.querySelector('[data-hh-lb-close]').focus(); }
    });

    var x0 = null;
    lb.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
    lb.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      if (Math.abs(dx) > 48) show(dx < 0 ? at + 1 : at - 1);
      x0 = null;
    }, { passive: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
